"use client";

import { useState } from "react";
import { GripVertical, List, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    AccordionAppearance,
    AccordionBlock,
    AccordionItem,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedGridGap } from "../../hooks/useGridGap";
import {
    ACCORDION_CONFIG,
    resolveAccordionAppearance,
    createAccordionItem,
} from "../../lib/blockRegistry";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "");

export default function AccordionSettings({ block }: { block: AccordionBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const setBlockGridGap = useLomdaV3Editor((s) => s.setBlockGridGap);
    const gridGap = useResolvedGridGap(block.id);

    const { items } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveAccordionAppearance(block.data.appearance, theme);
    const update = (patch: Partial<AccordionBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setItems = (next: AccordionItem[]) => update({ items: next });

    return (
        <div className="flex items-center gap-3">
            {/* ── 1. Common block settings ── */}
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t("lomda-v3-editor.interactive.accordion")}
                </span>
                <WidthControl blockId={block.id} />
                <div className="flex shrink-0 items-center gap-2">
                    <span className="whitespace-nowrap text-sm text-foreground">
                        {t("lomda-v3-editor.settings.grid-gap")}
                    </span>
                    <div className="flex h-9 w-16 items-center rounded-lg border px-2 text-sm text-foreground">
                        <input
                            type="number"
                            min={0}
                            value={gridGap}
                            onChange={(e) =>
                                setBlockGridGap(
                                    block.id,
                                    Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                            }
                            className="w-full bg-transparent text-center outline-none"
                        />
                    </div>
                </div>
            </div>

            <ToolbarDivider />

            {/* ── 2. Block-specific settings ── */}
            <div className="flex items-center gap-3">
                <AppearancePopover
                    appearance={appearance}
                    onChange={(patch) =>
                        update({ appearance: { ...appearance, ...patch } })
                    }
                    onReset={() => update({ appearance: undefined })}
                />
                <ManageItemsPopover items={items} onChange={setItems} />
            </div>
        </div>
    );
}

function ManageItemsPopover({
    items,
    onChange,
}: {
    items: AccordionItem[];
    onChange: (items: AccordionItem[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...items];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <List className="size-5" />
                    {t("lomda-v3-editor.accordion.manage-items")}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[412px] rounded-2xl p-4">
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.accordion.manage-items")}
                    </PopoverTitle>
                    <div className="flex flex-col gap-1">
                        {items.map((it, i) => (
                            <div
                                key={it.id}
                                draggable
                                onDragStart={() => setDragIndex(i)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                    if (dragIndex !== null) move(dragIndex, i);
                                    setDragIndex(null);
                                }}
                                className="flex items-center gap-2.5 rounded-lg px-1 py-1.5"
                            >
                                <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                                <span className="text-sm text-primary">
                                    {i + 1}
                                </span>
                                <input
                                    value={stripHtml(it.title)}
                                    onChange={(e) =>
                                        onChange(
                                            items.map((x) =>
                                                x.id === it.id
                                                    ? { ...x, title: e.target.value }
                                                    : x
                                            )
                                        )
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.accordion.title-placeholder"
                                    )}
                                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        onChange(
                                            items.filter((x) => x.id !== it.id)
                                        )
                                    }
                                    className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:text-destructive"
                                >
                                    <Trash2 className="size-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange([...items, createAccordionItem()])}
                        className="flex items-center gap-2 self-start rounded-lg text-sm text-muted-foreground transition hover:text-primary"
                    >
                        <Plus className="size-4" />
                        {t("lomda-v3-editor.accordion.add")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function AppearancePopover({
    appearance,
    onChange,
    onReset,
}: {
    appearance: AccordionAppearance;
    onChange: (patch: Partial<AccordionAppearance>) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.accordion.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.accordion.appearance")}
                    </PopoverTitle>
                    {ACCORDION_CONFIG.appearanceGroups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                const apKey =
                                    field.key as keyof AccordionAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "switch" ? (
                                            <Switch
                                                checked={Boolean(value)}
                                                onCheckedChange={(v) =>
                                                    onChange({ [apKey]: v })
                                                }
                                            />
                                        ) : field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
